import { ItemWrapper } from "@Components/forecast/hourly/ItemWrapper";
import { Representation } from "@Components/forecast/hourly/NavigationContainer";
import {
  MAX_NUMBER_OF_DAYS_FOR_FORECAST,
  NUMBER_OF_DAYS_FOR_FORECAST_IN_FUTURE,
} from "@Constants/forecast";
import { useForecastAppContext } from "@Context/ForecastAppContext/ForecastAppContext";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import {
  Intervals,
  OneHourInterval,
  ThreeHourInterval,
} from "@Models/Interval";
import {
  formatAsIsoStyleDate,
  formatAsWeekday,
  getFormattedTimestring,
} from "@Utils/date";
import { getIntervalStartIndicesPerDay } from "@Utils/interval";
import classNames from "classnames";
import { Fragment, RefObject, useState } from "react";

type IntervalContainer = {
  intervals: Intervals;
  representation: Representation;
  dayRefs: React.RefObject<
    Record<Representation, RefObject<HTMLLIElement | null>[]>
  >;
};

const DAY_SEPARATOR_WIDTH = 1;

export const IntervalContainer = ({
  intervals,
  representation,
  dayRefs,
}: IntervalContainer) => {
  const { t, currentLocale } = useTranslations();
  const { forecastPointWeek } = useForecastAppContext();
  const [timezone] = useState<string>(
    forecastPointWeek?.geolocation?.timezone ?? "Europe/Zurich",
  );
  // If we have only have 7 days in the forecast, the switch from 3h to 1h happens by 2 days instead of 3
  const intervalTypeSwitchIndex = (() => {
    const daysLength = forecastPointWeek?.days.length;
    return daysLength === NUMBER_OF_DAYS_FOR_FORECAST_IN_FUTURE ? 2 : 3;
  })();

  const intervalIndicesPerDay = getIntervalStartIndicesPerDay(
    intervals,
    timezone,
  );
  const dayIndices = Object.keys(intervalIndicesPerDay)
    .map((day) => parseInt(day))
    .slice(0, MAX_NUMBER_OF_DAYS_FOR_FORECAST);

  const getDayLabel = (
    dayIndex: number,
    interval: OneHourInterval | ThreeHourInterval,
  ) => {
    let label = formatAsWeekday(interval.date_time, timezone, currentLocale);

    if (dayIndex === intervalTypeSwitchIndex && interval.type === "3h") {
      label = `${label} (${interval.type} ${t("forecast.intervals")})`;
    }

    return label;
  };

  // First, we iterate through the days, then through the intervals for that day
  return dayIndices.map((dayIndex) => {
    const intervalsForDay: Intervals = intervals.slice(
      intervalIndicesPerDay[dayIndex],
      intervalIndicesPerDay[Math.min(dayIndex + 1, dayIndices.length)],
    );

    if (intervalsForDay.length === 0) {
      return null;
    }

    const referenceInterval = intervalsForDay[0];

    const dayIdentifier = formatAsIsoStyleDate(referenceInterval.date_time);
    const dayLabel = getDayLabel(dayIndex, referenceInterval);
    const hasIntervalTypeChangeSeparator = dayIndex === intervalTypeSwitchIndex;

    return (
      <Fragment key={dayIndex}>
        {hasIntervalTypeChangeSeparator && (
          <div className="m:z-2 m:w-6 m:border-0 m:border-l m:border-solid m:border-warmgrey-200 m:bg-warmgrey-10 m:dark:border-blue-850 m:dark:bg-blue-950"></div>
        )}

        {intervalsForDay.map((interval, intervalIndex) => {
          const intervalIdentifier = `${dayIdentifier}-${intervalIndex}`;
          const intervalLabel = getFormattedTimestring(
            interval.date_time,
            timezone,
            currentLocale,
          );

          const hasDayLabel = intervalIndex === 0;
          const isIntervalLabelBold = dayIndex === 0 && intervalIndex === 0;
          const hasIntervalDivider =
            intervalIndex !== intervalsForDay.length - 1;

          return (
            <Fragment key={`${dayIndex}-${intervalIndex}`}>
              {hasDayLabel && (
                <li
                  ref={dayRefs.current?.[representation]?.[dayIndex]}
                  style={{ borderRightWidth: `${DAY_SEPARATOR_WIDTH}px` }}
                  className={classNames(
                    "m:border-0 m:border-solid m:border-warmgrey-200 m:dark:border-blue-850",
                    {
                      "m:z-2": hasIntervalTypeChangeSeparator,
                    },
                  )}
                  data-day={dayIdentifier}>
                  <h3
                    className="m:md:text-sm m:absolute m:-top-4 m:m-0 m:p-0 m:text-base m:leading-[15px] m:font-medium m:tracking-0.2 m:whitespace-nowrap m:text-blue-950 m:antialiased m:lg:-top-4 m:lg:leading-[17px] m:lg:tracking-0.4 m:dark:text-neutral-white"
                    data-testid={`hourly-navigation-${representation}-day-label`}>
                    {dayLabel}
                  </h3>
                </li>
              )}
              <li
                className="m:m-0 m:list-item m:h-full m:w-[64px] m:shrink-0 m:p-0 m:antialiased m:lg:w-[80px]"
                data-interval={intervalIdentifier}
                data-interval-type={interval.type}
                data-testid={`hourly-navigation-${representation}-item`}>
                <ItemWrapper
                  label={intervalLabel}
                  intervalType={interval.type}
                  hour={interval}
                  representation={representation}
                  isLabelBold={isIntervalLabelBold}
                  hasDivider={hasIntervalDivider}
                />
              </li>
            </Fragment>
          );
        })}
      </Fragment>
    );
  });
};

IntervalContainer.displayName = "IntervalContainer";
